import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {differenceInDays, isAfter, isBefore} from 'date-fns';
import {
  type DateRange,
  type Matcher,
  dateMatchModifiers,
  dateLib,
} from 'react-day-picker';
import {useEvent} from '../../hooks';
import {formatDate, resetTime, toDate, DEFAULT_DISPLAY_FORMAT} from './dateUtils';
import type {DateInput} from './dateUtils';

type UseValidateDatesProps = {
  minDays?: number;
  maxDays?: number;
  minDate?: DateInput;
  maxDate?: DateInput;
  disabledDays?: Matcher | Matcher[];
  onError?: (error: string) => void;
  onSuccess?: () => void;
  displayFormat?: string;
};

function isAfterMax(date: Date, maxDate: Date) {
  return isAfter(resetTime(date), resetTime(maxDate));
}

function isBeforeMin(date: Date, minDate: Date) {
  return isBefore(resetTime(date), resetTime(minDate));
}

export function useValidateDates({
  minDays,
  maxDays,
  minDate,
  maxDate,
  disabledDays,
  onError,
  onSuccess,
  displayFormat,
}: UseValidateDatesProps) {
  const {t} = useTranslation();
  const handleError = useEvent(onError);
  const handleSuccess = useEvent(onSuccess);
  const errorFormatter = React.useMemo(
    () => formatDate(displayFormat ?? DEFAULT_DISPLAY_FORMAT),
    [displayFormat],
  );

  const validateDates = React.useCallback(
    (dates?: DateRange) => {
      const startDate = dates?.from;
      const endDate = dates?.to;
      if (!startDate || !endDate) return true;

      if (
        disabledDays &&
        (dateMatchModifiers(startDate, disabledDays, dateLib) ||
          dateMatchModifiers(endDate, disabledDays, dateLib))
      ) {
        handleError(t('wrong_period_of_dates'));
        return false;
      }

      if (isAfter(startDate, endDate)) {
        handleError(t('wrong_period_of_dates'));
        return false;
      }

      if (maxDays && differenceInDays(endDate, startDate) + 1 > maxDays) {
        handleError(t('date_range_max_days_error', {count: maxDays}));
        return false;
      }

      if (minDays && differenceInDays(endDate, startDate) < minDays) {
        handleError(t('date_range_min_days_error', {count: minDays}));
        return false;
      }

      const min = toDate(minDate);
      const max = toDate(maxDate);

      if (min && isBeforeMin(startDate, min)) {
        handleError(t('past_dates', {minDate: errorFormatter(resetTime(min))}));
        return false;
      }

      if (max && (isAfterMax(startDate, max) || isAfterMax(endDate, max))) {
        handleError(t('future_dates', {maxDate: errorFormatter(resetTime(max))}));
        return false;
      }

      handleSuccess();
      return true;
    },
    [
      disabledDays,
      errorFormatter,
      handleError,
      handleSuccess,
      maxDate,
      maxDays,
      minDate,
      minDays,
      t,
    ],
  );

  return {validateDates};
}
